import { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Center, Spinner, Text } from '@chakra-ui/react';
import { DataTable, type Column } from '@themes/aegix';
import { useDistrictReadiness } from '../../hooks/queries/useReunification';
import type { DistrictReadinessItem } from '../../types/ReunificationTypes';

interface ReadinessRow {
  [key: string]: unknown;
  id: string;
  school_name: string;
  plan_count: number;
  ready_plans: string;
  staff_assigned: number;
  last_drill: string;
  readiness_score: number;
}

function ReadinessScoreBadge({ score }: { score: number }) {
  let color: string;
  let bg: string;

  if (score < 50) {
    color = 'red.700';
    bg = 'red.100';
  } else if (score <= 80) {
    color = 'yellow.700';
    bg = 'yellow.100';
  } else {
    color = 'green.700';
    bg = 'green.100';
  }

  return (
    <Box display="inline-block" px={2} py={0.5} borderRadius="md" bg={bg}>
      <Text fontSize="sm" fontWeight="600" color={color}>
        {score}%
      </Text>
    </Box>
  );
}

function transformReadiness(items: DistrictReadinessItem[], neverText: string): ReadinessRow[] {
  return items.map((item, idx) => ({
    id: `${item.school_name}-${idx}`,
    school_name: item.school_name,
    plan_count: item.plan_count,
    ready_plans: `${item.ready_plans} / ${item.plan_count}`,
    staff_assigned: item.staff_assigned,
    last_drill: item.last_drill_date
      ? new Date(item.last_drill_date).toLocaleDateString()
      : neverText,
    readiness_score: item.readiness_score,
  }));
}

export function DistrictReadiness() {
  const { t } = useTranslation();
  const { data, isLoading, error } = useDistrictReadiness();
  const [sortColumn, setSortColumn] = useState<string>();
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc' | null>(
    null,
  );
  const [filterValues, setFilterValues] = useState<Record<string, string>>({});

  const columns: Column<ReadinessRow>[] = useMemo(
    () => [
      { key: 'school_name', label: t('nav.sites', 'School'), sortable: true, filterable: true, width: '220px' },
      { key: 'plan_count', label: t('reunification.plans', 'Plans'), sortable: true, width: '100px' },
      { key: 'ready_plans', label: t('reunification.readyPlans', 'Ready Plans'), sortable: false, width: '120px' },
      {
        key: 'staff_assigned',
        label: t('reunification.staffAssigned', 'Staff Assigned'),
        sortable: true,
        width: '140px',
      },
      {
        key: 'last_drill',
        label: t('events.lastDrill', 'Last Drill'),
        sortable: true,
        filterable: true,
        width: '140px',
      },
      {
        key: 'readiness_score',
        label: t('documents.readinessScore', 'Readiness Score'),
        sortable: true,
        width: '160px',
        render: (row) => <ReadinessScoreBadge score={row.readiness_score} />,
      },
    ],
    [t],
  );

  const handleSort = useCallback(
    (column: string) => {
      if (sortColumn === column) {
        setSortDirection((prev) =>
          prev === 'asc' ? 'desc' : prev === 'desc' ? null : 'asc',
        );
        if (sortDirection === 'desc') setSortColumn(undefined);
      } else {
        setSortColumn(column);
        setSortDirection('asc');
      }
    },
    [sortColumn, sortDirection],
  );

  const handleFilterChange = useCallback((column: string, value: string) => {
    setFilterValues((prev) => ({ ...prev, [column]: value }));
  }, []);

  const handleFilterClear = useCallback((column: string) => {
    setFilterValues((prev) => {
      const next = { ...prev };
      delete next[column];
      return next;
    });
  }, []);

  const rows = useMemo(
    () => (data ? transformReadiness(data, t('common.never', 'Never')) : []),
    [data, t],
  );

  const filteredRows = useMemo(() => {
    const activeFilters = Object.entries(filterValues).filter(([, v]) => v);
    if (activeFilters.length === 0) return rows;
    return rows.filter((row) =>
      activeFilters.every(([col, val]) => {
        const cellValue = String((row as Record<string, unknown>)[col] ?? '').toLowerCase();
        return cellValue.includes(val.toLowerCase());
      }),
    );
  }, [rows, filterValues]);

  if (isLoading) {
    return (
      <Center py={12}>
        <Spinner size="lg" color="button.primary" />
      </Center>
    );
  }

  if (error) {
    return (
      <Center py={12}>
        <Text color="text.danger">
          {error instanceof Error
            ? error.message
            : t('documents.failedLoadReadiness', 'Failed to load readiness data')}
        </Text>
      </Center>
    );
  }

  if (rows.length === 0) {
    return (
      <Box px={5} py={5}>
        <Center py={12}>
          <Text color="text.secondary">{t('documents.noReadinessDataAvailable', 'No readiness data available')}</Text>
        </Center>
      </Box>
    );
  }

  return (
    <Box px={5} py={5}>
      <DataTable
        columns={columns}
        data={filteredRows}
        keyExtractor={(row) => row.id}
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        onSort={handleSort}
        filterValues={filterValues}
        onFilterChange={handleFilterChange}
        onFilterClear={handleFilterClear}
      />
    </Box>
  );
}
